import type { ClickstreamPayload } from '@zg-rentals/clickstream';
import { getAnalyticsErrorType } from '@zg-rentals/rentals-analytics';
import { logError } from '@zg-rentals/log-error';

import { trackHotpadsClickstreamEvent } from '../index';
import { getCurrentThemeFields } from './themeState';
import type {
  HotpadsClickstreamListing,
  HotpadsContactRequestForm,
  HotpadsNextAttribution,
  HotpadsPropertyInfo,
} from './types';

type HotpadsTriggerType = 'impression' | 'interaction' | 'view';

type HotpadsEventTrigger = {
  type: HotpadsTriggerType;
  location: string;
  source: string;
  object: string;
};

export type HotpadsEventDefinition = {
  key: string;
  eventTypeId: string;
  eventTypeVersionId: string;
  eventTemplateId: string;
  eventTemplateVersionId: string;
  legacySource: string;
  legacyExport: string;
  trigger: HotpadsEventTrigger;
  semanticEvent: string;
};

type HotpadsEventOverrides = {
  listing?: HotpadsClickstreamListing | null;
  rentalSubmitId?: string | null;
  triggerLocation?: string;
  triggerObject?: string;
  triggerSource?: string;
};

export type HotpadsEventPayload = ClickstreamPayload<
  Partial<HotpadsPropertyInfo & HotpadsContactRequestForm> & {
    uncategorized?: Partial<HotpadsNextAttribution['uncategorized']>;
  }
>;

const NULL_PROPERTY_INFO_VALUES: HotpadsPropertyInfo = {
  property_info: {
    listing_alias: null,
    lot_id: null,
    listing_status_cd: null,
    is_paid_ind: null,
    property_type_cd: null,
    listing_type_cd: null,
  },
};

function isEmptyListing(listing: HotpadsClickstreamListing | null | undefined): listing is null | undefined {
  return listing == null || Object.keys(listing).length === 0;
}

function normalizeTriggerLocation(triggerLocation: string): string {
  return triggerLocation === 'home_details_halo' ? 'home_details' : triggerLocation;
}

export function defineHotpadsEvent<TEvent extends HotpadsEventDefinition>(definition: TEvent): TEvent {
  return definition;
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function toHotpadsClickstreamListing(listing: unknown): HotpadsClickstreamListing | null {
  if (!listing || typeof listing !== 'object') {
    return null;
  }

  const candidate = listing as Record<string, unknown>;
  const aliasEncoded = optionalString(candidate.aliasEncoded);
  const maloneLotIdEncoded = optionalString(candidate.maloneLotIdEncoded) ?? optionalString(candidate.lotIdEncoded);

  if (!aliasEncoded || !maloneLotIdEncoded) {
    return null;
  }

  return {
    active: optionalBoolean(candidate.active),
    aliasEncoded,
    isDemoted: optionalBoolean(candidate.isDemoted),
    isPaidAndNotDemoted: optionalBoolean(candidate.isPaidAndNotDemoted),
    isPaidMultifamily: optionalBoolean(candidate.isPaidMultifamily),
    listingType: optionalString(candidate.listingType),
    maloneLotIdEncoded,
    propertyType: optionalString(candidate.propertyType),
    units: Array.isArray(candidate.units)
      ? candidate.units.map(toHotpadsClickstreamListing).filter((unit) => unit !== null)
      : undefined,
  };
}

export function getPropertyInfo(listing?: HotpadsClickstreamListing | null): HotpadsPropertyInfo {
  try {
    if (isEmptyListing(listing)) {
      return NULL_PROPERTY_INFO_VALUES;
    }

    const listingForAnalytics = listing.units?.[0] ?? listing;

    return {
      property_info: {
        listing_alias: listingForAnalytics.aliasEncoded,
        lot_id: listingForAnalytics.maloneLotIdEncoded,
        listing_status_cd: listingForAnalytics.active == null ? '' : String(listingForAnalytics.active),
        is_paid_ind: listingForAnalytics.isPaidMultifamily ?? null,
        property_type_cd: listingForAnalytics.propertyType ?? '',
        listing_type_cd: listingForAnalytics.listingType ?? '',
      },
    };
  } catch (error) {
    logError({ error, errorType: getAnalyticsErrorType('getPropertyInfo'), context: { argumentPassed: listing } });

    return NULL_PROPERTY_INFO_VALUES;
  }
}

export function getContactRequestForm(rentalSubmitId?: string | null): HotpadsContactRequestForm {
  return {
    contact_request_form: {
      rental_submit_id: rentalSubmitId ?? null,
    },
  };
}

export function getEnvelope(data: {
  eventId: string;
  eventVersion: string;
  templateId?: string;
  templateVersion?: string;
}) {
  return {
    event_type_id: data.eventId,
    event_type_version_id: data.eventVersion,
    event_template_id: data.templateId || '',
    event_template_version_id: data.templateVersion || '',
  };
}

export function withHotpadsNextAttribution<TPayload extends HotpadsEventPayload>(
  payload: TPayload,
): TPayload & HotpadsNextAttribution {
  return {
    ...payload,
    uncategorized: {
      ...payload.uncategorized,
      ...getCurrentThemeFields(),
      nextjs_rewrite: true,
    },
  };
}

export function trackHotpadsEvent(payload: HotpadsEventPayload): void {
  trackHotpadsClickstreamEvent(withHotpadsNextAttribution(payload));
}

export function trackHotpadsEventDefinition(
  definition: HotpadsEventDefinition,
  overrides: HotpadsEventOverrides = {},
): void {
  trackHotpadsEvent({
    envelope: getEnvelope({
      eventId: definition.eventTypeId,
      eventVersion: definition.eventTypeVersionId,
      templateId: definition.eventTemplateId,
      templateVersion: definition.eventTemplateVersionId,
    }),
    clickstream_trigger: {
      trigger_type_nm: definition.trigger.type,
      trigger_location_nm: normalizeTriggerLocation(overrides.triggerLocation ?? definition.trigger.location),
      trigger_source_nm: overrides.triggerSource ?? definition.trigger.source,
      trigger_object_nm: overrides.triggerObject ?? definition.trigger.object,
    },
    semantic: {
      semantic_event_nm: definition.semanticEvent,
      topic_tag_txt: [],
    },
    ...(overrides.listing !== undefined ? getPropertyInfo(overrides.listing) : {}),
    ...(overrides.rentalSubmitId !== undefined ? getContactRequestForm(overrides.rentalSubmitId) : {}),
  });
}
